import { Breakpoint } from '@mui/material/styles';
import { ReactNode, Ref } from 'react';
import { KitDetails } from '../../../../services/modules/kit/kitDetails/kitDetails.types';
import { OrderProductError } from '../../../../services/modules/order-product/order-product.types';
import { AllowChangeGrid } from '../../../../services/modules/product-colors-packs/product-colors-packs.types';
import {
  GetSuggestedProductsResponse,
  SuggestedProductsToCustomer,
} from '../../../../services/modules/products/products.types';
import { OrderBookOrder } from '../../../../stores/OrderBook/orderBook.types';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import {
  AvailabilityEnum,
  CompactAvailability,
  ESColorClassification,
  ESProductSearch,
  ESProductTag,
} from '../../ProductTypes';
import { ESProductSearchState } from '../../details/productDetails.state';

export enum ProductDetailsEntityType {
  PRODUCT = 'PRODUCT',
  KIT = 'KIT',
}

interface ProductDetailsEntity {
  type: ProductDetailsEntityType.PRODUCT;
  data: ESProductSearch;
}

interface KitDetailsEntity {
  type: ProductDetailsEntityType.KIT;
  data: KitDetails;
}

export type DetailsModalEntity = ProductDetailsEntity | KitDetailsEntity;

export interface SimplePrice {
  wholesalePrice: number;
}

export interface ProductDetailsContentPricesProps {
  productDetailsEntityType?: ProductDetailsEntityType;
  prices: SimplePrice[];
}

export interface ProductDetailsDTO {
  productName: string;
  productCode: string;
  productId: string;
  productColorName: string;
  productColorCode: string;
  productColorId: string;
  productColorIsImage: boolean;
  productColorImageUrl?: string;
  productColorHexCode?: string;
  productColorMediaUrl: string | null;
  allowChangePreOrderGrid: AllowChangeGrid;
  allowChangeImmediateGrid: AllowChangeGrid;
  futureStockId: string | null;
  futureStockDeliveryWindowId: string | null;
  deliveryWindowId: string | null;
  deliveryWindowName: string | null;
  deliveryWindowStartDate: string | null;
  deliveryWindowEndDate: string | null;
  availability: AvailabilityEnum;
  isAvailableForCustomer: boolean;
  orderId?: string;
}

export interface GetProductDetailsEmptyAvailabilitiesOutput {
  orderId: string;
  productColorId: string;
}

export interface ProductDetailsGroupSection {
  orderId?: string;
  emptyOrderItems: string[];
  productsCount: number;
}

export interface ProductDetailsContentParams {
  product: ESProductSearchState;
  suggestedProductsData?: GetSuggestedProductsResponse[];
  disableGrid?: boolean;
  isMobileHeaderFixed?: boolean;
  refElement?: React.MutableRefObject<HTMLDivElement | null>;
  listFooter?: ReactNode;
  headerTopPositionRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export interface IProductDetailsSectionHeader {
  product?: ESProductSearchState;
  order?: OrderBookOrder;
}

export interface IProductDetailsCombination extends UsedInOfflineMode {
  selectedOrder: OrderBookOrder | undefined;
  allOrders: OrderBookOrder[];
  combination: ProductDetailsDTO;
  refetchUsingOrderIds: (orderIds: string[]) => Promise<void> | undefined;
  disableGrid?: boolean;
  getIsSuggestedProduct: (
    combination: ProductDetailsDTO,
    group?: SuggestedProductsToCustomer,
  ) => boolean;

  suggestedProducts: SuggestedProductsToCustomer[] | undefined;

  selloutQuantitySold?: number;
  isFetchingSellout: boolean;
  validationErrors?: OrderProductError[];
}

export interface VerifyProductDetailsFilterStateOnStorage {
  applyAllColors?: boolean;
  applyAllOrders?: boolean;
  applyRelatedProducts?: boolean;
  availabilityFilter?: CompactAvailability | undefined;
}

export interface ProductDetailsFilterStateOnStorage {
  isAllColorsFilterSelected?: boolean;
  isAllAvailabilitiesFilterSelected?: boolean;
  isAllOrdersFilterSelected?: boolean;
  isRelatedProductsFilterSelected?: boolean;
  availabilityFilter?: CompactAvailability | undefined;
}

export interface WidthSetterFunctionsParams {
  activeBreakpoint: Breakpoint;
  horizontalTabletWidth: boolean;
  gridQuantity: number;
}

export interface ContainerWidths {
  bigWidth: { width: number; maxWidth: number };
  smallWidth: { width: number; maxWidth: number };
  tabletSmallWidth: { width: number; maxWidth: number };
  tabletBigWidth: { width: number; maxWidth: number };
}

export enum PriceTypesEnum {
  RETAIL_PRICE = 'retailPrice',
  WHOLESALE_PRICE = 'wholesalePrice',
  PROMOTIONAL_PRICE = 'promotionalPrice',
}

export type GetMinMaxPrices =
  | {
      min: number;
      max: number;
    }
  | undefined;

export interface ProductDetailsHeaderProps {
  name: string;
  code: string;
  prices: SimplePrice[];
  allColorIds?: string[];
  referenceCode?: string;
  productColorName?: string;
  productColorCode?: string;
  isMobileHeaderFixed?: boolean;
  tags?: Array<ESColorClassification | ESProductTag>;
  titleRef?: Ref<HTMLDivElement>;
}
